import { useState, useEffect } from "react";
import { api } from "../api/staff";
import { Search, Shield, Check, Info } from "lucide-react";
import { PageHeader } from "../components/common/PageHeader";
import { Badge } from "../components/common/Badge";
import { cn } from "../lib/utils";

interface RoleMatrixItem {
  role: string;
  permissions: string[];
}

interface MatrixResponse {
  roles: RoleMatrixItem[];
  allPermissions: string[];
}

function humanize(perm: string) {
  const parts = perm.split(".");
  if (parts.length < 2) return perm;
  const action = parts[parts.length - 1] || "";
  const module = parts.slice(0, -1).join(".");
  const actionMap: Record<string, string> = {
    view: "Consulter",
    create: "Créer",
    edit: "Modifier",
    delete: "Supprimer",
    grant: "Attribuer",
    manage: "Gérer",
    validate: "Valider",
    revoke: "Révoquer",
    export: "Exporter",
    resolve: "Résoudre",
  };
  const act = actionMap[action] || action;
  return `${act} (${module})`;
}

export function PermissionsPage() {
  const [data, setData] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedModule, setSelectedModule] = useState<string>("all");

  useEffect(() => {
    api<MatrixResponse>("/staff/rbac/matrix")
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        Chargement des permissions…
      </div>
    );
  }

  const allModules = Array.from(
    new Set(data.allPermissions.map((p) => p.split(".")[0]).filter((m): m is string => Boolean(m)))
  );

  const filteredPermissions = data.allPermissions.filter((p) => {
    const matchSearch = p.toLowerCase().includes(search.toLowerCase());
    const matchModule = selectedModule === "all" || p.startsWith(`${selectedModule}.`);
    return matchSearch && matchModule;
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        title="Matrice des Permissions"
        description="Consultez et inspectez l'ensemble des droits attribués aux rôles dans le Backoffice."
      />

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher une permission…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Module filter tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          <button
            type="button"
            onClick={() => setSelectedModule("all")}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors",
              selectedModule === "all"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "bg-card border border-border text-muted-foreground hover:bg-accent"
            )}
          >
            Tous les modules
          </button>
          {allModules.map((mod) => (
            <button
              key={mod}
              type="button"
              onClick={() => setSelectedModule(mod)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors",
                selectedModule === mod
                  ? "bg-primary text-primary-foreground shadow-xs"
                  : "bg-card border border-border text-muted-foreground hover:bg-accent"
              )}
            >
              {mod}
            </button>
          ))}
        </div>
      </div>

      {/* Permissions Matrix Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-6 py-4 font-semibold text-muted-foreground">Permission</th>
                <th className="px-6 py-4 font-semibold text-muted-foreground">Action Clé</th>
                {data.roles.map((r) => (
                  <th key={r.role} className="px-6 py-4 font-semibold text-center capitalize text-muted-foreground">
                    {r.role}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPermissions.map((perm) => (
                <tr key={perm} className="hover:bg-muted/20 transition-colors">
                  <td className="px-6 py-3.5">
                    <div className="font-medium text-foreground">{humanize(perm)}</div>
                    <div className="text-xs font-mono text-muted-foreground mt-0.5">{perm}</div>
                  </td>
                  <td className="px-6 py-3.5">
                    <Badge variant="outline" className="font-mono text-[11px]">
                      {perm.split(".")[0]}
                    </Badge>
                  </td>
                  {data.roles.map((r) => {
                    const has = r.permissions.includes("*") || r.permissions.includes(perm);
                    return (
                      <td key={r.role} className="px-6 py-3.5 text-center">
                        {has ? (
                          <div className="w-6 h-6 mx-auto rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                            <Check className="w-3.5 h-3.5" />
                          </div>
                        ) : (
                          <span className="text-muted-foreground/30 font-bold">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
